import { useState } from 'react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { Download, FileSpreadsheet, FileText, ClipboardList } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { usePayroll } from '@/context/AppContext';
import { useToast } from '@/hooks/useToast';
import { processSalary } from '@/lib/payroll';

export function ExportPage() {
  const { payrollData, period, companyName, rawById, salaryFile, attendanceFile } = usePayroll();
  const { show } = useToast();
  const [busy, setBusy] = useState(false);

  if (!payrollData) {
    return (
      <div className="space-y-5 animate-slide-up">
        <h2 className="text-2xl font-extrabold flex items-center gap-2">
          <Download size={22} className="text-fuchsia-500" />
          تصدير البيانات
        </h2>
        <Card className="text-center !p-12">
          <p className="text-slate-600 dark:text-slate-300">احسب المرتبات أولاً</p>
        </Card>
      </div>
    );
  }

  const exportPayrollXLSX = async () => {
    setBusy(true);
    try {
      // ✅ تعريف الأعمدة (الترتيب + العرض + النوع)
      const cols: Array<{ key: string; label: string; w: number; type: 'text' | 'int' | 'dec' | 'money' }> = [
        { key: 'ID',                                label: 'ID',                                   w: 8,  type: 'text' },
        { key: 'الاسم',                             label: 'الاسم',                                w: 26, type: 'text' },
        { key: 'الوظيفة',                           label: 'الوظيفة',                              w: 16, type: 'text' },
        { key: 'يومية',                             label: 'يومية (ج)',                            w: 12, type: 'money' },
        { key: 'أجر الساعة',                        label: 'أجر الساعة (ج)',                       w: 13, type: 'money' },
        { key: 'أيام الحضور',                       label: 'أيام الحضور',                           w: 11, type: 'dec' },
        { key: 'إجمالي الإضافي (ساعة)',             label: 'إجمالي الإضافي (ساعة)',                w: 14, type: 'dec' },
        { key: 'أيام جزاء التأخير',                 label: 'جزاء تأخير (يوم)',                     w: 12, type: 'dec' },
        { key: 'أيام جزاء الخروج المبكر',           label: 'جزاء خروج مبكر (يوم)',                w: 14, type: 'dec' },
        { key: 'أيام جزاء النسيان',                 label: 'جزاء نسيان (يوم)',                     w: 12, type: 'dec' },
        { key: 'دقائق التأخير',                     label: 'دقائق تأخير',                          w: 11, type: 'int' },
        { key: 'دقائق الخروج المبكر',               label: 'دقائق خروج مبكر',                      w: 13, type: 'int' },
        { key: 'تعديلات إدارية (يوم)',              label: 'تعديلات إدارية (يوم)',                 w: 14, type: 'dec' },
        { key: 'قيمة التعديلات الإدارية',          label: 'قيمة التعديل الإداري (ج)',             w: 15, type: 'money' },
        { key: 'أجر الحضور الأساسي',                label: 'أجر الحضور الأساسي (ج)',               w: 15, type: 'money' },
        { key: 'أجر الساعات الإضافية',              label: 'أجر الساعات الإضافية (ج)',             w: 15, type: 'money' },
        { key: 'قيمة خصم التأخير',                  label: 'خصم تأخير (ج)',                        w: 13, type: 'money' },
        { key: 'قيمة خصم الخروج المبكر',            label: 'خصم خروج مبكر (ج)',                    w: 14, type: 'money' },
        { key: 'قيمة خصم النسيان',                  label: 'خصم نسيان (ج)',                        w: 13, type: 'money' },
        { key: 'صافي قبل التعديل الإداري',          label: 'صافي قبل التعديل الإداري (ج)',         w: 17, type: 'money' },
        { key: 'صافي بعد التعديل الإداري',          label: 'صافي بعد التعديل الإداري (ج)',         w: 17, type: 'money' },
      ];
      const numericKeys = cols.filter((c) => c.type !== 'text').map((c) => c.key);

      // ✅ تنسيقات exceljs
      const FMT_MONEY = '#,##0.00';
      const FMT_DEC = '#,##0.00';
      const FMT_INT = '#,##0';
      const thin = () => ({
        top: { style: 'thin' as const, color: { argb: 'FFB0B0B0' } },
        bottom: { style: 'thin' as const, color: { argb: 'FFB0B0B0' } },
        left: { style: 'thin' as const, color: { argb: 'FFB0B0B0' } },
        right: { style: 'thin' as const, color: { argb: 'FFB0B0B0' } },
      });
      const thick = () => ({
        top: { style: 'medium' as const, color: { argb: 'FF000000' } },
        bottom: { style: 'medium' as const, color: { argb: 'FF000000' } },
        left: { style: 'medium' as const, color: { argb: 'FF000000' } },
        right: { style: 'medium' as const, color: { argb: 'FF000000' } },
      });
      const styleHeader = {
        font: { bold: true, size: 12, color: { argb: 'FFFFFFFF' }, name: 'Cairo' },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF1F4E78' } },
        alignment: { horizontal: 'center' as const, vertical: 'middle' as const, wrapText: true },
        border: thick(),
        protection: { locked: true },
      };
      const styleText = {
        font: { size: 11, name: 'Cairo', color: { argb: 'FF000000' } },
        alignment: { horizontal: 'right' as const, vertical: 'middle' as const },
        border: thin(),
        protection: { locked: true },
      };
      // ✅ الاسم والوظيفة (B, C) بولد
      const styleTextBold = {
        font: { size: 11, bold: true, name: 'Cairo', color: { argb: 'FF000000' } },
        alignment: { horizontal: 'right' as const, vertical: 'middle' as const },
        border: thin(),
        protection: { locked: true },
      };
      const styleAltTextBold = {
        font: { size: 11, bold: true, name: 'Cairo', color: { argb: 'FF000000' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF4F8FC' } },
        alignment: { horizontal: 'right' as const, vertical: 'middle' as const },
        border: thin(),
        protection: { locked: true },
      };
      // ✅ عمود التعديلات الإدارية (M) — خلفية سماوي + خط أحمر + مفتوح للكتابة
      const styleAdminAdj = {
        font: { size: 11, bold: true, name: 'Cairo', color: { argb: 'FFB91C1C' } }, // أحمر
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFE0F2FE' } }, // سماوي فاتح
        alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
        border: {
          top: { style: 'medium' as const, color: { argb: 'FF0EA5E9' } },
          bottom: { style: 'medium' as const, color: { argb: 'FF0EA5E9' } },
          left: { style: 'medium' as const, color: { argb: 'FF0EA5E9' } },
          right: { style: 'medium' as const, color: { argb: 'FF0EA5E9' } },
        },
        numFmt: FMT_DEC,
        protection: { locked: false },
      };
      const styleAdminAdjAlt = {
        font: { size: 11, bold: true, name: 'Cairo', color: { argb: 'FFB91C1C' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFBAE6FD' } }, // سماوي أغمق شوية للصفوف المتناوبة
        alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
        border: {
          top: { style: 'medium' as const, color: { argb: 'FF0EA5E9' } },
          bottom: { style: 'medium' as const, color: { argb: 'FF0EA5E9' } },
          left: { style: 'medium' as const, color: { argb: 'FF0EA5E9' } },
          right: { style: 'medium' as const, color: { argb: 'FF0EA5E9' } },
        },
        numFmt: FMT_DEC,
        protection: { locked: false },
      };
      const styleNum = (z: string) => ({
        font: { size: 11, name: 'Cairo', color: { argb: 'FF000000' } },
        alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
        border: thin(),
        numFmt: z,
        protection: { locked: true },
      });
      const styleAltText = {
        font: { size: 11, name: 'Cairo', color: { argb: 'FF000000' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF4F8FC' } },
        alignment: { horizontal: 'right' as const, vertical: 'middle' as const },
        border: thin(),
        protection: { locked: true },
      };
      const styleAltNum = (z: string) => ({
        font: { size: 11, name: 'Cairo', color: { argb: 'FF000000' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF4F8FC' } },
        alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
        border: thin(),
        numFmt: z,
        protection: { locked: true },
      });
      const styleTextLocked = {
        ...styleText,
        protection: { locked: true },
      };
      const styleTextBoldLocked = {
        ...styleTextBold,
        protection: { locked: true },
      };
      const styleAltTextLocked = {
        ...styleAltText,
        protection: { locked: true },
      };
      const styleAltTextBoldLocked = {
        ...styleAltTextBold,
        protection: { locked: true },
      };
      // ✅ صف الإجمالي: نفس خط المحتوى (11pt) بدل 15 + مقفول
      const styleTotalText = {
        font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Cairo' },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF111827' } },
        alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
        border: thick(),
        protection: { locked: true },
      };
      const styleTotalNum = (z: string) => ({
        font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Cairo' },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF111827' } },
        alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
        numFmt: z,
        border: thick(),
        protection: { locked: true },
      });

      // ✅ بناء البيانات + مجاميع الأعمدة
      const totals: Record<string, number> = {};
      numericKeys.forEach((k) => (totals[k] = 0));

      const dataRows: Array<Record<string, any>> = payrollData
        .map((d: any) => {
          const before =
            d['صافي قبل التعديل الإداري'] ??
            d['صافي القبض المستحق'] - (d['قيمة التعديلات الإدارية'] || 0);
          const after = d['صافي القبض المستحق'];
          const row: Record<string, any> = {
            'ID': d.ID,
            'الاسم': d.Name,
            'الوظيفة': d.Job,
            'يومية': d['Salary / Day'],
            'أجر الساعة': d['Rate / Hour'],
            'أيام الحضور': d['أيام الحضور المستحقة'],
            'إجمالي الإضافي (ساعة)': d['إجمالي ساعات الإضافي'],
            'أيام جزاء التأخير': d['أيام جزاء التأخير'],
            'أيام جزاء الخروج المبكر': d['أيام جزاء الخروج المبكر'],
            'أيام جزاء النسيان': d['أيام جزاء النسيان'],
            'دقائق التأخير': d['دقائق التأخير'],
            'دقائق الخروج المبكر': d['دقائق الخروج المبكر'],
            'تعديلات إدارية (يوم)': d['تعديلات إدارية (بالأيام)'],
            'قيمة التعديلات الإدارية': d['قيمة التعديلات الإدارية'] || 0,
            'أجر الحضور الأساسي': d['أجر الحضور الأساسي'],
            'أجر الساعات الإضافية': d['أجر الساعات الإضافية'],
            'قيمة خصم التأخير': d['قيمة خصم التأخير'],
            'قيمة خصم الخروج المبكر': d['قيمة خصم الخروج المبكر'] || 0,
            'قيمة خصم النسيان': d['قيمة خصم النسيان'],
            'صافي قبل التعديل الإداري': before,
            'صافي بعد التعديل الإداري': after,
          };
          numericKeys.forEach((k) => {
            const v = Number(row[k] ?? 0);
            if (!Number.isNaN(v)) totals[k] += v;
          });
          return row;
        })
        // ✅ ترتيب أبجدي بالاسم (يدعم العربي)
        .sort((a, b) => String(a['الاسم'] || '').localeCompare(String(b['الاسم'] || ''), 'ar'));
      // ✅ صف الإجمالي (للعرض فقط — القيم الفعلية هتبقى formulas)
      const totalRow: Record<string, any> = {
        'ID': '',
        'الاسم': `📊 الإجمالي (${payrollData.length} موظف)`,
        'الوظيفة': '',
      };
      numericKeys.forEach((k) => (totalRow[k] = totals[k]));

      // ✅ تحويل رقم العمود لحرف (A, B, C, ... AA, AB)
      const colLetter = (idx: number) => {
        let s = '';
        let n = idx;
        while (n >= 0) {
          s = String.fromCharCode(65 + (n % 26)) + s;
          n = Math.floor(n / 26) - 1;
        }
        return s;
      };
      // ✅ خريطة الأعمدة بالأحرف (للـ formulas)
      const colMap: Record<string, string> = {};
      cols.forEach((c, i) => (colMap[c.key] = colLetter(i)));

      // ✅ إنشاء Workbook و Worksheet
      const wb = new ExcelJS.Workbook();
      wb.creator = companyName || 'Payroll App';
      wb.created = new Date();
      const ws = wb.addWorksheet('Payroll', {
        views: [{ state: 'frozen', xSplit: 3, ySplit: 1, activeCell: 'D2' }],
      });

      // ✅ عرض الأعمدة
      ws.columns = cols.map((c) => ({ width: c.w }));

      // ✅ صف الهيدر
      const headerRow = ws.addRow(cols.map((c) => c.label));
      headerRow.height = 32;
      headerRow.eachCell((cell: ExcelJS.Cell) => {
        cell.style = styleHeader;
      });

      // ✅ صفوف البيانات — الاسم/الوظيفة بولد + الأعمدة المحسوبة كـ formulas
      dataRows.forEach((r, idx) => {
        const isAlt = idx % 2 === 1;
        const excelRow = idx + 2; // الصف في Excel (1-based, بعد الهيدر)
        const rowValues: any[] = cols.map((c) => {
          if (c.type === 'text') return r[c.key] ?? '';

          // ✅ أعمدة محسوبة = Excel formulas
          const R = excelRow;
          if (c.key === 'قيمة التعديلات الإدارية') {
            const f = `${colMap['تعديلات إدارية (يوم)']}${R}*${colMap['يومية']}${R}`;
            return { formula: f, result: Number(r[c.key] ?? 0) };
          }
          if (c.key === 'أجر الحضور الأساسي') {
            const f = `${colMap['أيام الحضور']}${R}*${colMap['يومية']}${R}`;
            return { formula: f, result: Number(r[c.key] ?? 0) };
          }
          if (c.key === 'أجر الساعات الإضافية') {
            const f = `${colMap['إجمالي الإضافي (ساعة)']}${R}*${colMap['أجر الساعة']}${R}`;
            return { formula: f, result: Number(r[c.key] ?? 0) };
          }
          if (c.key === 'قيمة خصم التأخير') {
            const f = `${colMap['أيام جزاء التأخير']}${R}*${colMap['يومية']}${R}`;
            return { formula: f, result: Number(r[c.key] ?? 0) };
          }
          if (c.key === 'قيمة خصم الخروج المبكر') {
            const f = `${colMap['أيام جزاء الخروج المبكر']}${R}*${colMap['يومية']}${R}`;
            return { formula: f, result: Number(r[c.key] ?? 0) };
          }
          if (c.key === 'قيمة خصم النسيان') {
            const f = `${colMap['أيام جزاء النسيان']}${R}*${colMap['يومية']}${R}`;
            return { formula: f, result: Number(r[c.key] ?? 0) };
          }
          if (c.key === 'صافي قبل التعديل الإداري') {
            // أجر الحضور + أجر الإضافي - خصم تأخير - خصم خروج - خصم نسيان
            const f = `${colMap['أجر الحضور الأساسي']}${R}+${colMap['أجر الساعات الإضافية']}${R}-${colMap['قيمة خصم التأخير']}${R}-${colMap['قيمة خصم الخروج المبكر']}${R}-${colMap['قيمة خصم النسيان']}${R}`;
            return { formula: f, result: Number(r[c.key] ?? 0) };
          }
          if (c.key === 'صافي بعد التعديل الإداري') {
            const f = `${colMap['صافي قبل التعديل الإداري']}${R}+${colMap['قيمة التعديلات الإدارية']}${R}`;
            return { formula: f, result: Number(r[c.key] ?? 0) };
          }

          // باقي الأعمدة الرقمية (إدخال)
          const v = r[c.key];
          return v === '' || v == null ? 0 : Number(v) || 0;
        });
        const row = ws.addRow(rowValues);
        row.height = 20;
        row.eachCell((cell: ExcelJS.Cell, colNumber: number) => {
          const c = cols[colNumber - 1];
          // ✅ عمود التعديلات الإدارية (M) → سماوي/أحمر/مفتوح
          if (c.key === 'تعديلات إدارية (يوم)') {
            cell.style = isAlt ? styleAdminAdjAlt : styleAdminAdj;
            return;
          }
          if (c.type === 'text') {
            // ✅ الاسم (B) والوظيفة (C) بولد
            const isNameOrJob = colNumber === 2 || colNumber === 3;
            cell.style = isNameOrJob
              ? isAlt
                ? styleAltTextBold
                : styleTextBold
              : isAlt
              ? styleAltText
              : styleText;
          } else {
            const z = c.type === 'money' ? FMT_MONEY : c.type === 'dec' ? FMT_DEC : FMT_INT;
            cell.style = isAlt ? styleAltNum(z) : styleNum(z);
          }
        });
      });

      // ✅ صف الإجمالي — كل الأعمدة D-U كـ SUM formulas
      const totalRowIdx = dataRows.length + 2;
      const firstDataRow = 2;
      const lastDataRow = dataRows.length + 1;
      const totalValues: any[] = cols.map((c) => {
        if (c.key === 'ID' || c.key === 'الوظيفة') return '';
        if (c.key === 'الاسم') return totalRow['الاسم'];
        // ✅ كل الأعمدة الرقمية = SUM
        const letter = colMap[c.key];
        const f = `SUM(${letter}${firstDataRow}:${letter}${lastDataRow})`;
        return { formula: f, result: Number(totalRow[c.key] ?? 0) };
      });
      const totalRowExcel = ws.addRow(totalValues);
      totalRowExcel.height = 22;
      totalRowExcel.eachCell((cell: ExcelJS.Cell, colNumber: number) => {
        const c = cols[colNumber - 1];
        if (c.type === 'text') {
          cell.style = styleTotalText;
        } else {
          const z = c.type === 'money' ? FMT_MONEY : c.type === 'dec' ? FMT_DEC : FMT_INT;
          cell.style = styleTotalNum(z);
        }
      });

      // ✅ دمج أول 3 خلايا في صف الإجمالي (ID, الاسم, الوظيفة)
      ws.mergeCells(totalRowExcel.number, 1, totalRowExcel.number, 3);

      // ✅ Autofilter على كل الأعمدة
      ws.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: dataRows.length + 1, column: cols.length },
      };

      // ✅ حماية الشيت: كل الخلايا مقفولة ماعدا عمود التعديلات الإدارية
      await ws.protect('', {
        selectLockedCells: true,
        selectUnlockedCells: true,
        formatCells: true,
        formatColumns: true,
        formatRows: true,
        insertColumns: false,
        insertRows: false,
        insertHyperlinks: false,
        deleteColumns: false,
        deleteRows: false,
        sort: true,
        autoFilter: true,
        pivotTables: false,
      });

      // ✅ حفظ الملف
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll_${period.start || 'start'}_${period.end || 'end'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      show('تم تصدير ملف Excel ببيانات قابلة للتعديل');
    } catch (e: any) {
      show('خطأ: ' + e.message, 'error');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const exportAttendanceXLSX = async () => {
    setBusy(true);
    try {
      // ✅ نسخة خام من ملف البصمة — بدون أي معالجة
      if (!attendanceFile) {
        show('ارفع ملف البصمة أولاً', 'error');
        return;
      }
      const attBuf = await attendanceFile.arrayBuffer();
      const attWb = XLSX.read(attBuf, { type: 'array' });
      const fname = `attendance_${period.start || 'start'}_${period.end || 'end'}.xlsx`;
      XLSX.writeFile(attWb, fname);
      show('تم تصدير نسخة من ملف البصمة');
    } catch (e: any) {
      show('خطأ: ' + e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportSummaryCSV = async () => {
    setBusy(true);
    try {
      // ✅ نسخة خام من ملف البصمة بصيغة CSV
      if (!attendanceFile) {
        show('ارفع ملف البصمة أولاً', 'error');
        return;
      }
      const attBuf = await attendanceFile.arrayBuffer();
      const attWb = XLSX.read(attBuf, { type: 'array' });
      const firstSheetName = attWb.SheetNames[0];
      const ws = attWb.Sheets[firstSheetName];
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${period.start || 'start'}_${period.end || 'end'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      show('تم تصدير نسخة CSV من ملف البصمة');
    } catch (e: any) {
      show('خطأ: ' + e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 animate-slide-up">
      <h2 className="text-2xl font-extrabold flex items-center gap-2">
        <Download size={22} className="text-fuchsia-500" />
        تصدير البيانات
      </h2>

      <Card accent="fuchsia" padding="md">
        <p className="text-xs text-slate-600 dark:text-slate-400">
          صدّر بيانات المرتبات والبصمات بصيغ مختلفة للمشاركة مع المحاسب أو للأرشفة
        </p>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ExportCard
          icon={FileSpreadsheet}
          title="ملف Excel — المرتبات"
          desc={`${payrollData.length} موظف بكل التفاصيل والخصومات والتعديلات الإدارية`}
          color="from-emerald-500 to-teal-500"
          onClick={exportPayrollXLSX}
          loading={busy}
          tag="xlsx"
        />
        <ExportCard
          icon={ClipboardList}
          title="ملف Excel — البصمات"
          desc="كل سجلات الحضور والغياب والإضافي للموظفين كامل الفترة"
          color="from-sky-500 to-blue-500"
          onClick={exportAttendanceXLSX}
          loading={busy}
          tag="xlsx"
        />
        <ExportCard
          icon={FileText}
          title="ملخص CSV"
          desc="ملخص بسيط قابل للفتح في Excel أو Google Sheets"
          color="from-violet-500 to-purple-500"
          onClick={exportSummaryCSV}
          loading={busy}
          tag="csv"
        />
      </div>
    </div>
  );
}

function ExportCard({
  icon: Icon,
  title,
  desc,
  color,
  onClick,
  loading,
  tag,
}: {
  icon: any;
  title: string;
  desc: string;
  color: string;
  onClick: () => void;
  loading?: boolean;
  tag: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={`group text-right p-5 rounded-2xl bg-gradient-to-br ${color} text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm group-hover:scale-110 transition-transform">
          <Icon size={22} />
        </div>
        <Badge className="bg-white/20 text-white">{tag}</Badge>
      </div>
      <div className="font-extrabold text-base mb-1">{title}</div>
      <div className="text-xs opacity-90 leading-relaxed">{desc}</div>
      <div className="mt-3 text-xs font-bold flex items-center gap-1 opacity-95">
        <Download size={12} /> اضغط للتحميل
      </div>
    </button>
  );
}
